import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChipOption {
  id: string;
  name: string;
  iconKey: string | null;
}

interface CollapsibleChipListProps {
  options: ChipOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  maxVisible?: number;
  children: (visibleOptions: ChipOption[]) => React.ReactNode;
}

export const CollapsibleChipList: React.FC<CollapsibleChipListProps> = ({
  options,
  selected,
  onChange,
  maxVisible = 6,
  children
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hiddenCount = options.length - maxVisible;
  const shouldCollapse = options.length > maxVisible;
  const visibleOptions = isExpanded ? options : options.slice(0, maxVisible);

  // Count how many selected items are hidden
  const hiddenSelectedCount = !isExpanded
    ? selected.filter(id => !options.slice(0, maxVisible).some(opt => opt.id === id)).length
    : 0;

  return (
    <div className="space-y-3">
      {children(visibleOptions)}

      {shouldCollapse && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(
            "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
            "bg-muted/50 hover:bg-muted border border-border",
            "text-[#cfef00] hover:text-[#cfef00]/80"
          )}
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-4 w-4" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4" />
              Show All (+{hiddenCount})
              {hiddenSelectedCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-[#cfef00] text-black text-xs rounded-full">
                  {hiddenSelectedCount} selected
                </span>
              )}
            </>
          )}
        </button>
      )}
    </div>
  );
};
